import clsx from "clsx";
import React from "react";
import { Box, BoxProps } from "@mui/material";

import { ChevronDownIcon as CaretDownIcon } from "lucide-react";

import { GroupNavItemListItem } from "./GroupNavItemListItem";
import { useInfiniteQuery } from "@tanstack/react-query";
import GroupsService from "../../services/GroupsService";
import LoadingButton from "@mui/lab/LoadingButton";
import { GroupSort, GroupStatus } from "../../constants";
import { navItemListStyles } from "../item/NavItemStyles";

export interface GroupNavItemListProps extends BoxProps {}

export const NAVBAR_GROUP_LIST_QUERY_KEY = "get-current-user-groups";

const GROUP_NAV_ITEM_LIST_PAGE_SIZE = 5;

export function GroupNavItemList(props: GroupNavItemListProps) {
  const { className, ...other } = props;
  const s = navItemListStyles();

  const {
    data: groupsData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage: loading,
  } = useInfiniteQuery({
    queryKey: [NAVBAR_GROUP_LIST_QUERY_KEY],
    queryFn: ({ pageParam }) =>
      GroupsService.getGroups(
        GroupStatus.ACTIVE,
        GroupSort.NAME_ASC,
        pageParam ?? 1,
        GROUP_NAV_ITEM_LIST_PAGE_SIZE,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.hasNextPage ? pages.length + 1 : undefined;
    },
    placeholderData: {
      pages: [],
      pageParams: [],
    },
  });

  const groups = groupsData?.pages?.flatMap((page) => page.items);

  const handleMoreClick = React.useCallback(() => {
    if (!loading) {
      fetchNextPage();
    }
  }, [loading, fetchNextPage]);

  return (
    <Box className={clsx(s.root, className)} {...other}>
      {groups.map(({ id, name, slug }) => (
        <GroupNavItemListItem key={id} slug={slug} name={name} />
      ))}

      {hasNextPage && (
        <LoadingButton
          className={s.more}
          onClick={handleMoreClick}
          loading={loading}
          endIcon={<CaretDownIcon />}
        >
          <span>Show more</span>
        </LoadingButton>
      )}
    </Box>
  );
}
